import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import apiService from '../services/apiService';
import {
    DEFAULT_VALUES, SETTING_KEYS, resolveSettings, validateSettings,
} from '../utils/dealershipSettings';

/**
 * The "Dealership settings" admin form, shared by the v1 and v2 consoles:
 * loads one dealership's settings and history (admin endpoints by org_id),
 * keeps an editable draft, validates it like the backend registry, and saves
 * with optimistic concurrency (expected_version; a 409 means someone saved
 * in the meantime).
 *
 * The draft holds input strings, so a half-typed value is never coerced:
 *   { 'aging.boundaries': ['30', '90', '180'], 'pricing.gap_warn_pct': '5', … }
 *
 * Save sends ONE version-checked PUT { values: <changed keys>,
 * expected_version }. A key put back to its default while it is overridden
 * on the server goes as `null`, which removes the override in the same
 * atomic write (QA S-4). `onSaved(orgId)` runs after a successful save.
 */

export const CONFLICT_MESSAGE = 'Settings changed in the meantime – reload';

const toDraft = (values) => Object.fromEntries(Object.keys(DEFAULT_VALUES).map((key) => {
    const v = values[key];
    return [key, Array.isArray(v) ? v.map((n) => String(n)) : String(v)];
}));

// Plain decimals only: no exponent ("1e2"), hex or other forms Number()
// would also accept (QA P2-7).
const PLAIN_NUMBER = /^[+-]?(\d+(\.\d*)?|\.\d+)$/;

const parseNum = (s) => {
    const t = String(s ?? '').trim().replace(',', '.');
    if (t === '' || !PLAIN_NUMBER.test(t)) return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
};

/** The draft as typed values (null where a field does not parse). */
export const parseDraft = (draft) => Object.fromEntries(Object.entries(draft).map(([key, v]) => (
    [key, Array.isArray(v) ? v.map(parseNum) : parseNum(v)]
)));

const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

/** Per-key messages from a 422 body, whatever shape the API used. */
export const fieldErrorsFrom = (error) => {
    const body = error?.details || error?.response?.data || null;
    const detail = body?.detail ?? body;
    const out = {};
    if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
        const map = detail.errors || detail.fields || detail;
        Object.entries(map).forEach(([key, msg]) => {
            if (key in DEFAULT_VALUES) out[key] = Array.isArray(msg) ? msg.join(' ') : String(msg);
        });
    }
    if (Array.isArray(detail)) {
        detail.forEach((d) => {
            const key = (d?.loc || []).find((part) => typeof part === 'string' && part in DEFAULT_VALUES) || d?.key;
            if (key && key in DEFAULT_VALUES) out[key] = d.msg || d.message || 'Invalid value.';
        });
    }
    return out;
};

const messageOf = (error, fallback) => {
    const body = error?.details;
    if (typeof body?.detail === 'string') return body.detail;
    return error?.message || fallback;
};

export default function useDealershipSettingsAdmin(orgId, { onSaved } = {}) {
    const [payload, setPayload] = useState(null);
    const [draft, setDraft] = useState(() => toDraft(DEFAULT_VALUES));
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [conflict, setConflict] = useState(false);
    const [serverErrors, setServerErrors] = useState({});
    const [savedAt, setSavedAt] = useState(null);
    const seq = useRef(0);

    const server = useMemo(() => resolveSettings(payload), [payload]);

    const load = useCallback(async () => {
        if (!orgId) return;
        seq.current += 1;
        const mine = seq.current;
        setLoading(true);
        setLoadError(null);
        try {
            const [data, hist] = await Promise.all([
                apiService.getAdminDealershipSettings(orgId),
                apiService.getAdminDealershipSettingsHistory(orgId, 20).catch(() => []),
            ]);
            if (mine !== seq.current) return;
            setPayload(data || null);
            setDraft(toDraft(resolveSettings(data || null).values));
            setHistory(Array.isArray(hist) ? hist.slice(0, 20) : []);
            setConflict(false);
            setSaveError(null);
            setServerErrors({});
        } catch (err) {
            if (mine !== seq.current) return;
            setPayload(null);
            setLoadError(messageOf(err, 'Could not load the settings.'));
        } finally {
            if (mine === seq.current) setLoading(false);
        }
    }, [orgId]);

    useEffect(() => {
        setPayload(null);
        setHistory([]);
        setSavedAt(null);
        load();
    }, [load]);

    const parsed = useMemo(() => parseDraft(draft), [draft]);
    const errors = useMemo(() => ({ ...validateSettings(parsed, server.schema), ...serverErrors }), [parsed, server.schema, serverErrors]);
    const changedKeys = useMemo(
        () => Object.keys(DEFAULT_VALUES).filter((k) => !same(parsed[k], server.values[k])),
        [parsed, server.values],
    );
    const dirty = changedKeys.length > 0;
    // Whether any field differs from its default (Reset all has work to do).
    const anyNonDefault = Object.keys(DEFAULT_VALUES).some((k) => !same(parsed[k], server.defaults[k] ?? DEFAULT_VALUES[k]));
    const valid = Object.keys(validateSettings(parsed, server.schema)).length === 0;

    const setField = useCallback((key, value, index = null) => {
        setSavedAt(null);
        setServerErrors((prev) => {
            if (!(key in prev)) return prev;
            const next = { ...prev };
            delete next[key];
            return next;
        });
        setDraft((prev) => {
            if (index === null) return { ...prev, [key]: value };
            const list = [...prev[key]];
            list[index] = value;
            return { ...prev, [key]: list };
        });
    }, []);

    /** Put one field back to its default (saved with the rest). */
    const resetField = useCallback((key) => {
        const def = server.defaults[key] ?? DEFAULT_VALUES[key];
        setSavedAt(null);
        setDraft((prev) => ({ ...prev, [key]: Array.isArray(def) ? def.map(String) : String(def) }));
    }, [server.defaults]);

    /** Every field back to its default (staged; Save sends one PUT). */
    const resetAll = useCallback(() => {
        setSavedAt(null);
        setServerErrors({});
        setDraft(toDraft({ ...DEFAULT_VALUES, ...server.defaults }));
    }, [server.defaults]);

    // After a 409 the values on screen are stale: Discard loads the server's
    // current ones (and version), so the next save does not 409 again (P2-3).
    const discard = useCallback(() => {
        if (conflict) return load();
        setDraft(toDraft(server.values));
        setServerErrors({});
        setSaveError(null);
        return Promise.resolve();
    }, [conflict, load, server.values]);

    const save = useCallback(async () => {
        if (!orgId || saving || !valid || !dirty) return false;
        // Back at the default while overridden: null removes the override.
        const values = Object.fromEntries(changedKeys.map((k) => [
            k,
            same(parsed[k], server.defaults[k] ?? DEFAULT_VALUES[k]) && server.overridden.includes(k) ? null : parsed[k],
        ]));
        setSaving(true);
        setSaveError(null);
        setConflict(false);
        setServerErrors({});
        try {
            await apiService.saveAdminDealershipSettings(orgId, values, server.version);
            await load();
            setSavedAt(Date.now());
            if (onSaved) onSaved(orgId);
            return true;
        } catch (err) {
            if (err?.status === 409) {
                setConflict(true);
                setSaveError(CONFLICT_MESSAGE);
            } else if (err?.status === 422) {
                const fieldErrors = fieldErrorsFrom(err);
                setServerErrors(fieldErrors);
                setSaveError(Object.keys(fieldErrors).length ? 'Some values were not accepted. Check the marked fields.' : messageOf(err, 'The values were not accepted.'));
            } else if (err?.status === 403) {
                setSaveError('You may not change the settings of this dealership.');
            } else {
                setSaveError(messageOf(err, 'Could not save the settings. Please try again.'));
            }
            return false;
        } finally {
            setSaving(false);
        }
    }, [orgId, saving, valid, dirty, changedKeys, parsed, server, load, onSaved]);

    return {
        loading,
        loadError,
        loaded: Boolean(payload),
        server,
        canEdit: server.canEdit,
        draft,
        parsed,
        errors,
        changedKeys,
        dirty,
        anyNonDefault,
        valid,
        saving,
        saveError,
        conflict,
        savedAt,
        history,
        setField,
        resetField,
        resetAll,
        discard,
        save,
        reload: load,
    };
}

export { SETTING_KEYS };
